import { loadCheckoutImage as loadImage } from './checkoutDeadline.js';
import { printAreas, templatePresets } from '@/data/products';
import { createMockupArtworkCanvas } from '@/lib/mockupArtwork';

const GARMENT_SHADING_STRENGTH = 0.12;


function isSafeCanvasSrc(src) {
  if (!src || typeof src !== 'string') return false;
  if (src.startsWith('data:') || src.startsWith('blob:')) return true;
  if (src.startsWith('/')) return true;
  try {
    const u = new URL(src, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

// Compose base schematic (front/back) with the uploaded design placed into the overlay rect based on templatePresets or printAreas fallback.
// Returns a PNG data URL.
export async function composeMockupImage({ areaKey, baseImage, designUrl, template = 'tshirt', width = 800, height = 800, background = '#f6f7f9' }) {
  if (!areaKey || !baseImage || !designUrl) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Fill background
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);

    // Draw base schematic scaled to fit contain
    let baseImg = null;
    let basePlacement = null;
    try {
      if (!isSafeCanvasSrc(baseImage)) throw new Error('unsafe base image src');
      baseImg = await loadImage(baseImage);
    } catch {
      baseImg = null;
    }
    if (baseImg) {
      const baseRatio = baseImg.width / baseImg.height;
      const targetRatio = width / height;
      let dw, dh, dx, dy;
      if (baseRatio > targetRatio) {
        // limited by width
        dw = width; dh = Math.round(width / baseRatio); dx = 0; dy = Math.round((height - dh) / 2);
      } else {
        dh = height; dw = Math.round(height * baseRatio); dy = 0; dx = Math.round((width - dw) / 2);
      }
      ctx.drawImage(baseImg, dx, dy, dw, dh);
      basePlacement = { dx, dy, dw, dh };
    }

    // Compute overlay rect percentages
    const area = printAreas[areaKey] || {};
    const preset = (templatePresets?.[template] && templatePresets[template]?.[areaKey]) || null;
    const overlay = preset ? {
      x: (preset.x / 100) * width,
      y: (preset.y / 100) * height,
      w: (preset.w / 100) * width,
      h: (preset.h / 100) * height,
      rot: preset.rot || 0,
    } : {
      x: (area.overlayX / 800) * width,
      y: (area.overlayY / 800) * height,
      w: (area.overlayW / 800) * width,
      h: (area.overlayH / 800) * height,
      rot: area.overlayRot || 0,
    };

    // Draw design contained inside overlay rect with rotation. If the source fails to load
    // (for example when the uploaded file is a PDF), render a simple placeholder so the
    // mockup frame is not left empty.
    let designImg = null;
    try {
      if (!isSafeCanvasSrc(designUrl)) throw new Error('unsafe design src');
      designImg = await loadImage(designUrl);
    } catch (err) {
      designImg = null;
    }
    ctx.save();
    ctx.translate(overlay.x + overlay.w/2, overlay.y + overlay.h/2);
    if (overlay.rot) ctx.rotate((overlay.rot * Math.PI) / 180);
    if (designImg) {
      // fit contain into overlay
      const scale = Math.min(overlay.w / designImg.width, overlay.h / designImg.height);
      const dw2 = Math.round(designImg.width * scale);
      const dh2 = Math.round(designImg.height * scale);
      const artwork = createMockupArtworkCanvas(designImg, dw2, dh2);
      const artworkLayer = document.createElement('canvas');
      artworkLayer.width = width;
      artworkLayer.height = height;
      const artworkContext = artworkLayer.getContext('2d');
      artworkContext.translate(overlay.x + overlay.w / 2, overlay.y + overlay.h / 2);
      if (overlay.rot) artworkContext.rotate((overlay.rot * Math.PI) / 180);
      artworkContext.drawImage(artwork.canvas, -dw2 / 2, -dh2 / 2, dw2, dh2);

      // Draw the artwork normally so customer colors and white pixels stay intact.
      ctx.restore();
      ctx.drawImage(artworkLayer, 0, 0);

      // Reapply a small amount of the garment's own color/shading, masked by
      // the mockup artwork alpha. This visually seats the artwork in the fabric
      // without changing the stored customer file or multiplying the artwork itself.
      if (baseImg && basePlacement) {
        const shadingLayer = document.createElement('canvas');
        shadingLayer.width = width;
        shadingLayer.height = height;
        const shadingContext = shadingLayer.getContext('2d');
        shadingContext.drawImage(
          baseImg,
          basePlacement.dx,
          basePlacement.dy,
          basePlacement.dw,
          basePlacement.dh,
        );
        shadingContext.globalCompositeOperation = 'destination-in';
        shadingContext.drawImage(artworkLayer, 0, 0);

        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = GARMENT_SHADING_STRENGTH;
        ctx.drawImage(shadingLayer, 0, 0);
        ctx.restore();
      }

      return canvas.toDataURL('image/png');
    } else {
      // Draw placeholder rectangle and label for non-image uploads (PDFs)
      const pw = Math.floor(overlay.w * 0.9);
      const ph = Math.floor(overlay.h * 0.9);
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.fillRect(-pw/2, -ph/2, pw, ph);
      ctx.strokeRect(-pw/2, -ph/2, pw, ph);
      ctx.fillStyle = '#374151';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto';
      ctx.fillText('PDF preview not available', 0, 0);
    }
    ctx.restore();

    return canvas.toDataURL('image/png');
  } catch (e) {
    return null;
  }
}
